import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ticket,
  Trophy,
  Clock,
  ArrowRight,
  Sparkles,
  Hash,
  Crown,
  CalendarClock,
  BadgeCheck,
  Zap,
} from "lucide-react";
import Layout from "../components/Layout";
import Countdown from "../components/Countdown";
import PrelaunchBanner from "../components/PrelaunchBanner";
import { api } from "../lib/api";
import { formatEuros } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/e05ced9f-6148-4505-a7e4-6e3a7371c241/images/d3928ca63c8f6f55da42f4883b0d8da50a913975fc2b1f20f0bb3ede36b18a10.png";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenges, setChallenges] = useState(null);
  const [registered, setRegistered] = useState([]);
  const [winnings, setWinnings] = useState(null);
  const [qualifications, setQualifications] = useState(null);
  const [launchCfg, setLaunchCfg] = useState(null);
  const [code, setCode] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/launch/config");
        setLaunchCfg(data);
      } catch (e) {
        setLaunchCfg(null);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/challenges/public/list");
        setChallenges(data);
      } catch (e) {
        setChallenges([]);
      }
    })();
  }, []);

  // Load "Défis inscrits" (reserved code) only when authenticated.
  // Also re-poll every 30s when there's at least one SCHEDULED registered
  // challenge — so the server's "open" status flips in shortly after the
  // start time crosses. (The card itself flips its CTA every second via a
  // local Date.now() check; this re-poll just keeps the snapshot fresh.)
  useEffect(() => {
    if (!user || user === false) {
      setRegistered([]);
      setWinnings(null);
      setQualifications(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [reg, win, qual] = await Promise.all([
          api.get("/challenges/me/registered"),
          api.get("/play/me/pending-winnings"),
          api.get("/play/me/qualifications"),
        ]);
        if (!cancelled) {
          setRegistered(reg.data?.items || []);
          setWinnings(win.data || null);
          setQualifications(qual.data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setRegistered([]);
          setWinnings(null);
          setQualifications(null);
        }
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  const onCode = (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    navigate(`/play?code=${code.trim().toUpperCase()}`);
  };

  const buyGenericCode = () => {
    navigate("/acheter");
  };

  // IDs the user is already registered to — we de-dupe to avoid showing them
  // twice (once in "Défis inscrits", once in "Défis ouverts" / "À venir").
  const registeredIds = new Set(registered.map((c) => c.id));
  const all = challenges || [];
  // v2.45 — Simplified taxonomy: 4 top-level categories only.
  // Priority order (strict, mutually exclusive):
  //   1. Live events  → separate section (LiveEventsRow, no per-challenge data)
  //   2. Golden       → category='golden'
  //   3. Free         → entry_mode='free' OR price_amount==0
  //   4. Paid         → everything else (price_amount > 0)
  // Note: a finale (access_mode=qualified_only) with prize but 0 € entry stays
  // in "Free" — because the player CAN'T pay for it. Badges on the card
  // still surface "FINALE À CAGNOTTE" / "QUALIFICATIF" details.
  const active = all.filter(
    (c) => (c.status === "open" || c.status === "scheduled") && !registeredIds.has(c.id),
  );
  const isFreeEntry = (c) =>
    c.entry_mode === "free" || Number(c.price_amount || 0) === 0;
  const golden = active.filter((c) => c.category === "golden");
  const goldenIds = new Set(golden.map((c) => c.id));
  const freeChallenges = active.filter(
    (c) => !goldenIds.has(c.id) && isFreeEntry(c),
  );
  const paidChallenges = active.filter(
    (c) => !goldenIds.has(c.id) && !isFreeEntry(c),
  );
  const closed = all.filter(
    (c) => c.status === "finalized" || c.status === "pending_finalization" || c.closed === true,
  );

  return (
    <Layout>
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/30 to-[#05050A]/60" />
        <div className="absolute inset-0 grid-bg opacity-30" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-20 pb-16 sm:pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-5 sm:space-y-7 min-w-0">
              <h1
                data-testid="home-hero-title"
                className="font-display font-black uppercase tracking-tighter text-[2rem] sm:text-6xl lg:text-7xl leading-[0.95] break-words"
              >
                Joue,
                <br />
                <span className="text-cyan-400 neon-text-primary">Domine</span> le classement
                <br />
                et remporte la cagnotte
                <span className="text-fuchsia-400 neon-text-secondary"> !!!</span>
              </h1>
              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                Une plateforme de défis chronométrés. Tu paies, tu reçois un code personnel, tu joues
                seul pendant la fenêtre ouverte. Le podium est révélé à la fin.
              </p>

              <form
                onSubmit={onCode}
                className="flex items-center gap-2 panel rounded-2xl pl-3 sm:pl-5 pr-2 py-2 w-full max-w-md focus-within:border-cyan-400/70 focus-within:glow-primary transition-all"
              >
                <Hash className="w-5 h-5 text-cyan-400 shrink-0" />
                <input
                  data-testid="home-code-input"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 8))
                  }
                  placeholder="CODE PERSONNEL"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck="false"
                  className="flex-1 min-w-0 bg-transparent outline-none font-mono-stat text-[15px] sm:text-xl tracking-[0.15em] sm:tracking-[0.3em] placeholder:text-white/30"
                />
                <button
                  data-testid="home-code-submit"
                  type="submit"
                  disabled={code.length < 4}
                  className="shrink-0 px-3.5 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase text-xs sm:text-sm disabled:opacity-30 active:scale-95 transition-all"
                >
                  Jouer
                </button>
              </form>

              <button
                data-testid="home-buy-code-button"
                onClick={buyGenericCode}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black font-display font-bold uppercase text-sm tracking-wider active:scale-95 transition-all glow-primary"
              >
                <Ticket className="w-4 h-4" />
                Acheter un code
              </button>
            </div>

            <div className="lg:col-span-5">
              <div className="panel rounded-3xl p-8 scanlines relative">
                <div className="absolute -top-4 left-6 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-xs uppercase tracking-widest font-mono-stat text-cyan-200">
                  Comment ça marche
                </div>
                <ol className="space-y-4 mt-2" data-testid="how-it-works-list">
                  {[
                    ["1", "Choisis un défi", "Quiz unique ou série thématique"],
                    ["2", "Choisis ta formule", "Reçois tes codes"],
                    ["3", "Entre ton code", "Et joue seul en mode chrono"],
                    ["4", "Attends la fin", "Podium révélé à la fermeture"],
                    ["5", "Remporte la cagnotte", "Fais plaisir à ton banquier"],
                  ].map(([n, t, d]) => (
                    <li
                      key={n}
                      className="flex items-start gap-4"
                      data-testid={`how-step-${n}`}
                    >
                      <div className="w-9 h-9 shrink-0 rounded-full bg-cyan-400 text-black font-display font-black flex items-center justify-center">
                        {n}
                      </div>
                      <div>
                        <div className="font-display font-bold">{t}</div>
                        <div className="text-sm text-white/55">{d}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <PrelaunchBanner config={launchCfg} />
        {winnings && winnings.count > 0 && (
          <div
            data-testid="home-claim-banner"
            className="mb-10 rounded-2xl border border-amber-400/55 bg-gradient-to-r from-amber-400/15 via-amber-400/8 to-fuchsia-400/10 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 shadow-[0_0_45px_rgba(251,191,36,0.18)]"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-12 h-12 rounded-xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center shrink-0">
                <Trophy className="w-6 h-6 text-amber-300" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-mono-stat mb-1">
                  Tu as gagné !
                </div>
                <div className="font-display font-bold text-lg sm:text-xl truncate">
                  {winnings.count} gain{winnings.count > 1 ? "s" : ""} à réclamer ·{" "}
                  <span data-testid="home-claim-total" className="text-amber-300">
                    {formatEuros(winnings.total_amount)}
                  </span>
                </div>
                <div className="text-xs text-white/60 mt-0.5 truncate">
                  {winnings.items[0]?.challenge_title}
                  {winnings.count > 1 ? ` · +${winnings.count - 1} autre${winnings.count - 1 > 1 ? "s" : ""}` : ""}
                </div>
              </div>
            </div>
            <button
              data-testid="home-claim-cta"
              onClick={() => navigate("/mes-codes?filter=claims")}
              className="shrink-0 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-display font-bold uppercase tracking-wider text-sm active:scale-95 transition-all shadow-[0_0_25px_rgba(251,191,36,0.4)]"
            >
              Réclamer mes gains
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {qualifications && qualifications.count > 0 && (
          <div
            data-testid="home-qualifications-banner"
            className="mb-10 rounded-2xl border border-amber-400/60 bg-gradient-to-r from-amber-400/15 via-fuchsia-400/8 to-cyan-400/10 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 shadow-[0_0_45px_rgba(251,191,36,0.22)]"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-12 h-12 rounded-xl bg-amber-400/20 border border-amber-400/50 flex items-center justify-center shrink-0">
                <Crown className="w-6 h-6 text-amber-300" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-mono-stat mb-1">
                  Tu es qualifié !
                </div>
                <div className="font-display font-bold text-lg sm:text-xl truncate">
                  {qualifications.count} qualification{qualifications.count > 1 ? "s" : ""} en cours
                </div>
                <div className="text-xs text-white/60 mt-0.5 truncate">
                  Finale{qualifications.count > 1 ? "s" : ""} :{" "}
                  <span className="text-amber-200">
                    {qualifications.items[0]?.destination_challenge_title}
                  </span>
                  {qualifications.count > 1
                    ? ` · +${qualifications.count - 1} autre${qualifications.count - 1 > 1 ? "s" : ""}`
                    : ""}
                </div>
              </div>
            </div>
            <button
              data-testid="home-qualifications-cta"
              onClick={() => navigate("/mes-qualifications")}
              className="shrink-0 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-display font-bold uppercase tracking-wider text-sm active:scale-95 transition-all shadow-[0_0_25px_rgba(251,191,36,0.4)]"
            >
              Voir mes finales
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {registered.length > 0 && (
          <RegisteredRow
            title="Défis inscrits"
            items={registered}
            testid="registered-row"
          />
        )}
        <LiveEventsRow />
        <ChallengeRow
          title="👑 Golden Quizz"
          subtitle="Défis premium accessibles avec un Golden Code."
          testid="row-golden"
          items={golden}
          empty="Aucun Golden Quizz actif. Ouvre l'œil !"
          accent="amber"
        />
        <ChallengeRow
          title="🎫 Défis gratuits"
          subtitle="Quiz gratuits, qualificatifs et finales accessibles sans paiement."
          testid="row-free"
          items={freeChallenges}
          empty="Aucun défi gratuit ouvert. Reviens vite !"
          accent="cyan"
        />
        <ChallengeRow
          title="💶 Défis payants"
          subtitle="Défis accessibles avec un droit d'entrée ou un code acheté."
          testid="row-paid"
          items={paidChallenges}
          empty="Aucun défi payant en ce moment."
          accent="fuchsia"
        />
        <ChallengeRow
          title="✅ Terminés - classements disponibles"
          testid="row-closed"
          items={closed}
          empty="Pas encore de défis terminés."
          accent="amber"
        />
      </section>
    </Layout>
  );
}

function LiveEventsRow() {
  const navigate = useNavigate();
  return (
    <div className="mb-12" data-testid="row-live">
      <div className="flex items-end justify-between mb-5">
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
          📡 Événements Live
        </h2>
        <span className="text-xs uppercase tracking-[0.3em] text-white/40 font-mono-stat">
          Sur invitation
        </span>
      </div>
      <div
        className="rounded-2xl px-6 py-8 border border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-400/10 via-cyan-400/5 to-transparent flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-12 h-12 rounded-xl bg-fuchsia-400/20 border border-fuchsia-400/50 flex items-center justify-center shrink-0">
            <Zap className="w-6 h-6 text-fuchsia-300" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-lg sm:text-xl">
              Soirée quiz en salle ?
            </div>
            <div className="text-xs text-white/60 mt-0.5">
              Entre le code fourni par l&apos;animateur pour rejoindre la salle en direct.
            </div>
          </div>
        </div>
        <button
          data-testid="row-live-cta"
          onClick={() => navigate("/live")}
          className="shrink-0 px-5 py-3 rounded-xl bg-fuchsia-500 text-white font-display font-bold uppercase tracking-wider text-sm hover:bg-fuchsia-400 active:scale-95 transition-all"
        >
          Rejoindre le live
        </button>
      </div>
    </div>
  );
}

function ChallengeRow({ title, subtitle, items, empty, accent, testid }) {
  return (
    <div className="mb-12" data-testid={testid}>
      <div className="flex items-end justify-between mb-5 gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs text-white/50 mt-1 max-w-2xl">{subtitle}</p>
          )}
        </div>
        <span className="text-xs uppercase tracking-[0.3em] text-white/40 font-mono-stat shrink-0">
          {items.length} défi{items.length > 1 ? "s" : ""}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="panel rounded-2xl px-6 py-10 text-center text-white/40 font-mono-stat uppercase tracking-widest text-sm">
          {empty}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c) => (
            <ChallengeCard key={c.id} c={c} accent={accent} />
          ))}
        </div>
      )}
    </div>
  );
}

function RegisteredRow({ title, items, testid }) {
  return (
    <div className="mb-12" data-testid={testid}>
      <div className="flex items-end justify-between mb-5">
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-amber-200">
          {title}
        </h2>
        <span className="text-xs uppercase tracking-[0.3em] text-amber-300/70 font-mono-stat">
          {items.length} place{items.length > 1 ? "s" : ""} réservée{items.length > 1 ? "s" : ""}
        </span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c) => (
          <RegisteredCard key={c.id} c={c} />
        ))}
      </div>
    </div>
  );
}

function RegisteredCard({ c }) {
  const navigate = useNavigate();
  const isGolden = c.category === "golden";
  // Source of truth at first fetch — refreshed on tick below.
  const startsAtMs = c.starts_at ? new Date(c.starts_at).getTime() : 0;
  // Ticker: re-render every 1s as long as the challenge is still scheduled
  // so we can flip the CTA from "Disponible bientôt" to "Jouer maintenant"
  // the instant the countdown reaches zero — without any manual refresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (c.status === "open") return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [c.status]);
  // Local "open" derivation: API says open, OR start time has elapsed.
  const isOpen = c.status === "open" || (startsAtMs > 0 && Date.now() >= startsAtMs);
  const codeType = c.reserved_code_type || "standard";
  const handlePlay = (e) => {
    e.stopPropagation();
    if (!isOpen) return;
    navigate(`/play?code=${encodeURIComponent(c.reserved_code)}`);
  };
  return (
    <div
      data-testid={`registered-card-${c.id}`}
      onClick={() => navigate(`/challenges/${c.id}`)}
      className={`group cursor-pointer rounded-2xl p-6 border transition-all hover:-translate-y-1 ${
        isGolden
          ? "border-amber-400/55 bg-gradient-to-br from-amber-400/15 via-amber-400/5 to-transparent hover:shadow-[0_0_35px_rgba(251,191,36,0.3)]"
          : "border-cyan-400/40 bg-gradient-to-br from-cyan-400/10 via-cyan-400/5 to-transparent hover:border-cyan-400/80 hover:shadow-[0_0_30px_rgba(0,229,255,0.18)]"
      }`}
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <span
          data-testid={`registered-tag-${c.id}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.3em] font-display font-bold ${
            isGolden
              ? "bg-amber-400/15 border border-amber-400/60 text-amber-200"
              : "bg-cyan-400/15 border border-cyan-400/60 text-cyan-200"
          }`}
        >
          <BadgeCheck className="w-3 h-3" />
          Tu es inscrit
        </span>
        <span
          className={`text-[10px] uppercase tracking-[0.25em] font-mono-stat px-2 py-0.5 rounded-full ${
            isGolden
              ? "bg-amber-400/10 text-amber-300"
              : "bg-cyan-400/10 text-cyan-300"
          }`}
        >
          {codeType === "golden" ? "Golden" : "Standard"}
        </span>
      </div>

      <h3 className="font-display text-xl font-bold mb-1 line-clamp-2">{c.title}</h3>
      <div className="flex items-center gap-2 text-[11px] text-white/55 font-mono-stat uppercase tracking-widest mb-3">
        {c.type === "series" ? "Série" : "Quiz unique"} · {isOpen ? "Ouvert" : "À venir"}
      </div>

      <div className="rounded-xl bg-black/40 border border-white/10 px-3 py-2 mb-4 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-stat">
            Code réservé
          </div>
          <div
            data-testid={`registered-code-${c.id}`}
            className="font-mono-stat tracking-[0.25em] truncate text-white"
          >
            {c.reserved_code}
          </div>
        </div>
        {isGolden && <Crown className="w-4 h-4 text-amber-300 shrink-0" />}
      </div>

      <div className="space-y-1.5 mb-4">
        <div className="flex items-center gap-2 text-xs text-white/65 font-mono-stat">
          <CalendarClock className="w-3.5 h-3.5 text-cyan-300" />
          <span className="uppercase tracking-widest text-white/45">
            {isOpen ? "Fin :" : "Début :"}
          </span>
          <span className="text-white/85">
            {new Date(isOpen ? c.ends_at : c.starts_at).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
        <div
          data-testid={`registered-countdown-${c.id}`}
          className={`text-xs font-mono-stat ${
            isOpen ? "text-emerald-300" : "text-cyan-200"
          }`}
        >
          <Countdown
            target={isOpen ? c.ends_at : c.starts_at}
            kind={isOpen ? "closing" : "opening"}
            dataTestid={`registered-countdown-value-${c.id}`}
          />
        </div>
      </div>

      <PrizeBlock c={c} isGolden={isGolden} />

      {isOpen ? (
        <button
          data-testid={`registered-play-now-${c.id}`}
          onClick={handlePlay}
          className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-display font-bold uppercase tracking-wider text-sm active:scale-95 transition-all ${
            isGolden
              ? "bg-amber-400 hover:bg-amber-300 text-black shadow-[0_0_25px_rgba(251,191,36,0.4)]"
              : "bg-cyan-400 hover:bg-cyan-300 text-black glow-primary"
          }`}
        >
          {isGolden ? <Crown className="w-4 h-4" /> : <Ticket className="w-4 h-4" />}
          Jouer maintenant
        </button>
      ) : (
        <div
          data-testid={`registered-disabled-${c.id}`}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white/45 font-display font-bold uppercase tracking-wider text-sm"
        >
          <Clock className="w-4 h-4" />
          Disponible bientôt
        </div>
      )}
    </div>
  );
}

function ChallengeBadges({ c }) {
  // v2.39 — visual taxonomy: one primary badge + optional secondary line.
  // Priority order: FINALE > QUALIFICATIF > GOLDEN > CAGNOTTE > GRATUIT > STANDARD
  const isGolden = c.category === "golden";
  const isFree = c.entry_mode === "free" || Number(c.price_amount || 0) === 0;
  const isQualifierSource = c.reward_type === "qualification";
  const isFinaleDest = c.access_mode === "qualified_only";
  const hasPrize = Number(c.prize_pool_base || 0) > 0;
  const badges = [];
  if (isFinaleDest) {
    badges.push({
      key: "finale",
      label: hasPrize ? "FINALE À CAGNOTTE" : "FINALE",
      cls: "bg-amber-400/15 border-amber-400/50 text-amber-200",
      Icon: Crown,
    });
  } else if (isQualifierSource) {
    badges.push({
      key: "qualifier",
      label: isFree ? "QUALIFICATIF GRATUIT" : "QUALIFICATIF",
      cls: "bg-cyan-400/15 border-cyan-400/50 text-cyan-200",
      Icon: Sparkles,
    });
  } else if (isGolden) {
    badges.push({
      key: "golden",
      label: "GOLDEN QUIZZ",
      cls: "bg-amber-400/15 border-amber-400/50 text-amber-200",
      Icon: Crown,
    });
  } else if (hasPrize) {
    badges.push({
      key: "cagnotte",
      label: "CAGNOTTE",
      cls: "bg-emerald-400/15 border-emerald-400/50 text-emerald-200",
      Icon: Trophy,
    });
  } else if (isFree) {
    badges.push({
      key: "free",
      label: "GRATUIT",
      cls: "bg-fuchsia-400/15 border-fuchsia-400/50 text-fuchsia-200",
      Icon: Ticket,
    });
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {badges.map((b) => (
        <span
          key={b.key}
          data-testid={`challenge-card-${b.key}-badge-${c.id}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] uppercase tracking-widest font-display font-bold ${b.cls}`}
        >
          <b.Icon className="w-2.5 h-2.5" />
          {b.label}
        </span>
      ))}
    </div>
  );
}

function ChallengeSubtitle({ c }) {
  // v2.39 — one-line explainer under the title.
  const isFree = c.entry_mode === "free" || Number(c.price_amount || 0) === 0;
  const isQualifierSource = c.reward_type === "qualification";
  const isFinaleDest = c.access_mode === "qualified_only";
  const hasPrize = Number(c.prize_pool_base || 0) > 0;
  if (isFinaleDest) {
    return (
      <p
        data-testid={`challenge-card-subtitle-${c.id}`}
        className="text-xs text-amber-200/80 font-mono-stat uppercase tracking-widest mb-3"
      >
        Réservé aux joueurs qualifiés
        {hasPrize ? ` · Cagnotte ${formatEuros(c.prize_pool_base)}` : ""}
      </p>
    );
  }
  if (isQualifierSource) {
    const slots = Number(c.qualification_config?.qualified_count) || 0;
    const destTitle = c.qualification_config?.destination_challenge_title;
    const slotsLabel =
      slots === 1
        ? "1 place en finale à gagner"
        : slots > 1
        ? `${slots} places en finale à gagner`
        : "Qualification pour la finale";
    return (
      <p
        data-testid={`challenge-card-subtitle-${c.id}`}
        className="text-xs text-cyan-200/85 font-mono-stat uppercase tracking-widest mb-3"
      >
        {slotsLabel}
        {isFree ? " · Participation gratuite" : ""}
        {destTitle ? ` · Finale : ${destTitle}` : ""}
      </p>
    );
  }
  if (isFree) {
    return (
      <p
        data-testid={`challenge-card-subtitle-${c.id}`}
        className="text-xs text-fuchsia-200/85 font-mono-stat uppercase tracking-widest mb-3"
      >
        Participation gratuite
      </p>
    );
  }
  return null;
}

function ChallengeCard({ c, accent }) {
  const navigate = useNavigate();
  const accentMap = {
    cyan: { dot: "bg-cyan-400", border: "border-cyan-400/40 hover:border-cyan-400/80", text: "text-cyan-300" },
    fuchsia: { dot: "bg-fuchsia-400", border: "border-fuchsia-400/40 hover:border-fuchsia-400/80", text: "text-fuchsia-300" },
    amber: { dot: "bg-amber-400", border: "border-amber-400/40 hover:border-amber-400/80", text: "text-amber-300" },
  };
  const a = accentMap[accent];
  const isGolden = c.category === "golden";
  const isScheduled = c.status === "scheduled";
  const isClosed = c.status === "finalized" || c.status === "pending_finalization" || c.closed === true;
  // v2.46 — provisional leaderboard signal: user has completed the quiz but
  // it is still open. Show "Déjà joué" label + "Classement provisoire" CTA.
  const hasPlayed = !!c.my_completed_session_id;
  const isOpen = c.status === "open";
  const alreadyPlayedOpen = hasPlayed && isOpen;
  return (
    <div
      data-testid={`challenge-card-${c.id}`}
      onClick={() => {
        if (isClosed) {
          navigate(`/leaderboard/${c.id}`);
        } else if (alreadyPlayedOpen) {
          navigate(`/leaderboard/${c.id}`);
        } else {
          navigate(`/challenges/${c.id}`);
        }
      }}
      className={`group cursor-pointer rounded-2xl p-6 border transition-all hover:-translate-y-1 ${
        isGolden
          ? "border-amber-400/50 bg-gradient-to-br from-amber-400/10 via-amber-400/5 to-transparent hover:border-amber-400/80 hover:shadow-[0_0_35px_rgba(251,191,36,0.25)]"
          : `panel ${a.border}`
      }`}
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono-stat ${a.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${a.dot} ${c.status === "open" ? "pulse-ring" : ""}`} />
            {c.type === "series" ? "Série" : "Quiz unique"} · {c.status}
          </span>
          <ChallengeBadges c={c} />
          {alreadyPlayedOpen && (
            <span
              data-testid={`challenge-card-already-played-${c.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] uppercase tracking-widest font-display font-bold bg-cyan-400/15 border-cyan-400/50 text-cyan-200"
            >
              <BadgeCheck className="w-2.5 h-2.5" />
              Déjà joué
            </span>
          )}
        </div>
        <span className="text-xs font-mono-stat text-white/50">
          {c.access_mode === "qualified_only"
            ? "Qualifiés"
            : c.entry_mode === "free" || Number(c.price_amount || 0) === 0
            ? "Gratuit"
            : isGolden
            ? "Golden Code"
            : formatEuros(c.price_amount)}
        </span>
      </div>
      <h3 className="font-display text-xl font-bold mb-2 line-clamp-2">{c.title}</h3>
      <ChallengeSubtitle c={c} />
      {c.description && <p className="text-sm text-white/60 mb-4 line-clamp-2">{c.description}</p>}

      {/* Scheduled → "Avant ouverture" countdown to starts_at.
          Open      → "Avant fermeture" countdown to ends_at.
          Closed    → no countdown. */}
      {isScheduled && (
        <div className="space-y-1 mb-3" data-testid={`challenge-schedule-${c.id}`}>
          <div className="flex items-center gap-2 text-xs text-white/70 font-mono-stat">
            <CalendarClock className="w-3.5 h-3.5 text-fuchsia-300" />
            <span className="uppercase tracking-widest text-white/45">Début :</span>
            <span className="text-white/85">
              {new Date(c.starts_at).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          <div
            data-testid={`challenge-countdown-${c.id}`}
            className="text-xs font-mono-stat text-fuchsia-200"
          >
            <Countdown
              target={c.starts_at}
              kind="opening"
              dataTestid={`challenge-countdown-value-${c.id}`}
            />
          </div>
        </div>
      )}
      {c.status === "open" && (
        <div className="space-y-1 mb-3" data-testid={`challenge-closing-${c.id}`}>
          <div className="flex items-center gap-2 text-xs text-white/70 font-mono-stat">
            <Clock className="w-3.5 h-3.5 text-emerald-300" />
            <span className="uppercase tracking-widest text-white/45">Fin :</span>
            <span className="text-white/85">
              {new Date(c.ends_at).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          <div
            data-testid={`challenge-closing-countdown-${c.id}`}
            className="text-xs font-mono-stat text-emerald-200"
          >
            <Countdown
              target={c.ends_at}
              kind="closing"
              dataTestid={`challenge-closing-countdown-value-${c.id}`}
            />
          </div>
        </div>
      )}

      <PrizeBlock c={c} isGolden={isGolden} />
      <QualificationPrizeBlock c={c} />

      <div className="flex items-center gap-4 text-xs text-white/50 font-mono-stat flex-wrap">
        <span className="flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> {c.total_questions} Q
        </span>
        {isClosed && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(c.ends_at).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
        {isScheduled && (
          <span className="flex items-center gap-1 text-white/35">
            <Clock className="w-3 h-3" />
            Fin :{" "}
            {new Date(c.ends_at).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
            })}
          </span>
        )}
      </div>
      <div
        data-testid={`challenge-card-cta-${c.id}`}
        className="mt-4 flex items-center gap-1 text-sm font-display font-bold uppercase tracking-wider opacity-70 group-hover:opacity-100 transition-opacity"
      >
        {isClosed ? (
          <>
            <Trophy className="w-4 h-4" />
            Voir le classement final
          </>
        ) : alreadyPlayedOpen ? (
          <>
            <Trophy className="w-4 h-4" />
            Voir classement provisoire
          </>
        ) : (
          <>
            <Ticket className="w-4 h-4" />
            Participer
          </>
        )}
        <ArrowRight className="w-4 h-4 ml-auto group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  );
}

// Bloc cagnotte mis en avant — vendeur principal de la carte.
// Affiché uniquement si une cagnotte > 0 est définie sur le défi.
// Variante Golden = doré premium + glow ; sinon ambré standard.
function PrizeBlock({ c, isGolden }) {
  const amount = Number(c.current_prize_pool || 0);
  if (!amount || amount <= 0) return null;
  const winners = Number(c.winners_count || 1);
  return (
    <div
      data-testid={`challenge-card-prize-block-${c.id}`}
      className={`rounded-xl px-4 py-3 mb-4 border ${
        isGolden
          ? "border-amber-400/60 bg-gradient-to-br from-amber-400/20 via-amber-400/8 to-amber-500/5 shadow-[0_0_25px_rgba(251,191,36,0.18)]"
          : "border-amber-400/35 bg-gradient-to-br from-amber-400/12 via-amber-400/5 to-transparent"
      }`}
    >
      <div
        className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-mono-stat mb-1 ${
          isGolden ? "text-amber-300" : "text-amber-300/85"
        }`}
      >
        {isGolden ? <Crown className="w-3 h-3" /> : <Trophy className="w-3 h-3" />}
        {isGolden ? "Cagnotte Golden" : "À gagner"}
      </div>
      <div
        data-testid={`challenge-card-prize-amount-${c.id}`}
        className={`font-display font-black leading-none ${
          isGolden
            ? "text-3xl sm:text-4xl text-amber-300 neon-text-secondary"
            : "text-2xl sm:text-3xl text-amber-300"
        }`}
      >
        {formatEuros(amount)}
      </div>
      {winners > 1 && (
        <div
          data-testid={`challenge-card-winners-${c.id}`}
          className={`text-[10px] uppercase tracking-widest font-mono-stat mt-2 ${
            isGolden ? "text-amber-200/80" : "text-white/55"
          }`}
        >
          {winners} gagnants récompensés
        </div>
      )}
    </div>
  );
}

function QualificationPrizeBlock({ c }) {
  // v2.42 — Big visual block for FREE qualifier quizzes. Equivalent weight
  // to the cash PrizeBlock, so users understand the reward is a ticket, not
  // money. Only rendered when reward_type='qualification' AND no cash prize.
  if (c.reward_type !== "qualification") return null;
  if (Number(c.prize_pool_base || 0) > 0) return null; // handled by PrizeBlock
  const isFree = c.entry_mode === "free" || Number(c.price_amount || 0) === 0;
  const slots = Number(c.qualification_config?.qualified_count) || 0;
  const destTitle = c.qualification_config?.destination_challenge_title;
  const ticketLabel =
    slots > 0
      ? `${slots} ticket${slots > 1 ? "s" : ""}${destTitle ? " " + destTitle : ""}`
      : "1 place en finale";
  return (
    <div
      data-testid={`challenge-card-qualification-block-${c.id}`}
      className="rounded-xl px-4 py-3 mb-4 border border-cyan-400/45 bg-gradient-to-br from-cyan-400/12 via-fuchsia-400/6 to-transparent shadow-[0_0_25px_rgba(34,211,238,0.15)]"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-mono-stat mb-1 text-cyan-300">
        <Crown className="w-3 h-3" />
        {isFree ? "Gratuit · À gagner" : "À gagner"}
      </div>
      <div
        data-testid={`challenge-card-qualification-tickets-${c.id}`}
        className="font-display font-black leading-none text-2xl sm:text-3xl text-white"
      >
        {ticketLabel}
      </div>
      {destTitle && (
        <div
          data-testid={`challenge-card-qualification-dest-${c.id}`}
          className="text-[10px] uppercase tracking-widest font-mono-stat mt-2 text-cyan-200/85"
        >
          Qualification pour : {destTitle}
        </div>
      )}
    </div>
  );
}
